import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { hubClient } from '../../services/hubClient';
import { hubService } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import type { BotSystem } from '../../lib/botNav';
import { fmtTime } from '../../lib/jobView';
import { latestGate } from '../../lib/answerReviewView';
import {
  canKeepLatin,
  convertErrorText,
  kindLabel,
  latinShare,
  versionLine,
  versionNote,
  type Conversions,
  type CorrectionEvaluation,
  type ConvertResult,
} from '../../lib/latinView';
import { CorrectForm, CorrectionsView, EvaluationNote } from './CorrectionsView';
import { GlossaryPanel } from '../knowledge/GlossaryPanel';

type View = 'conversions' | 'corrections' | 'keep' | 'test';
const VIEW_LABEL: Record<View, string> = { conversions: 'Conversions', corrections: 'Corrections', keep: 'Keep Latin', test: 'Test' };
const DAYS = [7, 30, 90];
const SAVED = 'Saved as a checked conversion';
const KEPT = 'Kept Latin — live within about a minute';
const MAX_TEXT = 500;

/**
 * Quality › Latin text (Mungun/infra text-conversion review, P1): what customers typed in Latin and what the converter
 * made of it, most frequent first, and a box to test the converter with a message as a customer would type it.
 * P2: *Correct* on a row saves an operator correction for this bot; the *Corrections* tab lists and edits them.
 * P4: *Keep Latin* on a row adds the word to this bot's keep-Latin glossary; the *Keep Latin* tab is Knowledge › Glossary.
 */
export function LatinTextPanel({ system, botId }: { system: BotSystem; botId: string }) {
  const [view, setView] = useState<View>('conversions');
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Customers often type Mongolian in Latin letters. The bot converts each such message to Cyrillic before it searches
        knowledge, intents and blocks — a wrong word here lowers every match after it.
      </p>
      <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" role="tablist">
        {(['conversions', 'corrections', 'keep', 'test'] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`px-3 py-1 text-sm rounded ${view === v ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {VIEW_LABEL[v]}
          </button>
        ))}
      </div>
      {view === 'conversions' ? (
        <ConversionsView system={system} botId={botId} />
      ) : view === 'corrections' ? (
        <CorrectionsView system={system} botId={botId} />
      ) : view === 'keep' ? (
        <GlossaryPanel system={system} botId={botId} />
      ) : (
        <TestView system={system} botId={botId} />
      )}
    </div>
  );
}

function ConversionsView({ system, botId }: { system: BotSystem; botId: string }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<Conversions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [corrected, setCorrected] = useState<Map<string, CorrectionEvaluation | null | undefined>>(new Map());
  // *Looks right* (P3): sample key → saved | error text
  const [checked, setChecked] = useState<Record<string, string>>({});
  // *Keep Latin* (P4): row key → KEPT | error text
  const [kept, setKept] = useState<Record<string, string>>({});
  const gate = useRef(latestGate());

  const load = useCallback(async () => {
    const id = gate.current.begin();
    setError(null);
    setData(null);
    setOpen(null);
    setCorrecting(null);
    setCorrected(new Map());
    setKept({});
    setChecked({});
    try {
      const r = await hubClient.textConversions({ system, botId }, days);
      if (gate.current.isCurrent(id)) setData(r);
    } catch (e) {
      if (gate.current.isCurrent(id)) setError(errorText(e));
    }
  }, [system, botId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const drift = data ? versionNote(data.versions) : null;
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          Period
          <select className="border border-gray-300 rounded px-2 py-1" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </label>
        {data && <span className="text-gray-600">{latinShare(data.messages)}</span>}
      </div>
      {drift && <p className="text-sm rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800">{drift}</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}
      {!data && !error ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : data && data.rows.length === 0 ? (
        <p className="text-sm text-gray-500">No Latin messages in this period.</p>
      ) : data ? (
        <table className="w-full text-sm bg-white border border-gray-200 rounded-lg">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="p-2">Customer wrote</th>
              <th className="p-2">Became</th>
              <th className="p-2">How</th>
              <th className="p-2">Count</th>
              <th className="p-2">Last seen</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const key = `${r.raw}\u0000${r.out}\u0000${r.kind}`;
              return (
                <Fragment key={key}>
                  <tr className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(open === key ? null : key)}>
                    <td className="p-2 font-mono">{r.raw}</td>
                    <td className="p-2 font-mono">
                      {r.out}
                      {r.check && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-sans text-amber-800" title="Not a dictionary word — check it">
                          check
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-gray-600">{kindLabel(r.kind)}</td>
                    <td className="p-2">{r.count}</td>
                    <td className="p-2 text-gray-600">{fmtTime(r.lastSeen)}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {corrected.has(key) ? (
                        <span className="text-xs text-green-700">Corrected — live within about a minute</span>
                      ) : kept[key] === KEPT ? (
                        <span className="text-xs text-green-700">{KEPT}</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="text-blue-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCorrecting(correcting === key ? null : key);
                            }}
                          >
                            Correct
                          </button>
                          {canKeepLatin(r) && (
                            <button
                              type="button"
                              className="ml-3 text-blue-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                hubService
                                  .knowledge(system, botId, 'POST', 'glossary', { terms: [r.raw] })
                                  .then(() => setKept((k) => ({ ...k, [key]: KEPT })))
                                  .catch((err) => setKept((k) => ({ ...k, [key]: errorText(err) })));
                              }}
                            >
                              Keep Latin
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {kept[key] && kept[key] !== KEPT && (
                    <tr>
                      <td colSpan={6} className="px-2 pb-2 text-sm text-red-600">
                        {kept[key]}
                      </td>
                    </tr>
                  )}
                  {correcting === key && (
                    <tr className="bg-blue-50">
                      <td colSpan={6} className="p-2">
                        <CorrectForm
                          system={system}
                          botId={botId}
                          latin={r.raw}
                          cyrillic={r.out}
                          onDone={(evaluation) => {
                            setCorrecting(null);
                            setCorrected((c) => new Map(c).set(key, evaluation));
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  {corrected.has(key) && corrected.get(key) !== undefined && (
                    <tr>
                      <td colSpan={6} className="px-2 pb-2">
                        <EvaluationNote evaluation={corrected.get(key)} />
                      </td>
                    </tr>
                  )}
                  {open === key && (
                    <tr className="bg-gray-50">
                      <td colSpan={6} className="p-2">
                        <ul className="space-y-1">
                          {r.samples.map((s, i) => {
                            const sk = `${key}\u0000${i}`;
                            return (
                              <li key={i} className="text-xs">
                                <span className="text-gray-500">{fmtTime(s.at)}</span> <span className="font-mono">{s.question}</span>
                                {' → '}
                                <span className="font-mono">{s.normalized}</span>{' '}
                                {checked[sk] ? (
                                  <span className={checked[sk] === SAVED ? 'text-green-700' : 'text-red-700'}>{checked[sk]}</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="text-blue-700"
                                    title="The conversion is right: keep it as a checked conversion, so later corrections are tested against it"
                                    onClick={() =>
                                      void hubClient
                                        .textEvalConfirm({ system, botId }, { latin: s.question, expected: s.normalized })
                                        .then(() => setChecked((c) => ({ ...c, [sk]: SAVED })))
                                        .catch((e) => setChecked((c) => ({ ...c, [sk]: errorText(e) })))
                                    }
                                  >
                                    Looks right
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

function TestView({ system, botId }: { system: BotSystem; botId: string }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await hubClient.textConvert({ system, botId }, text));
    } catch (e) {
      setError(convertErrorText(errorText(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3">
      <p className="text-sm text-gray-600">Type a message exactly as a customer would, in Latin letters.</p>
      <textarea
        className="w-full border border-gray-300 rounded p-2 text-sm font-mono"
        rows={3}
        maxLength={MAX_TEXT}
        value={text}
        placeholder="zahialga ugmuur bna"
        onChange={(e) => setText(e.target.value)}
      />
      <button
        type="button"
        disabled={busy || !text.trim()}
        onClick={() => void run()}
        className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
      >
        {busy ? 'Converting…' : 'Convert'}
      </button>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {result && (
        <div className="space-y-2 rounded border border-gray-200 bg-white p-3 text-sm">
          <p className="font-mono text-base">{result.normalized}</p>
          {result.changes.length === 0 ? (
            <p className="text-gray-500">Nothing was changed.</p>
          ) : (
            <ul className="space-y-0.5">
              {result.changes.map((c, i) => (
                <li key={i}>
                  <span className="font-mono">{c.raw}</span> → <span className="font-mono">{c.out}</span>{' '}
                  <span className="text-gray-500">({kindLabel(c.kind)})</span>
                </li>
              ))}
            </ul>
          )}
          {result.suggestions.map((s, i) => (
            <p key={i} className="text-gray-600">
              Possible typo: <span className="font-mono">{s.raw}</span> → <span className="font-mono">{s.out}</span>
            </p>
          ))}
          {result.version && (
            <p className="text-xs text-gray-400">{versionLine(result.version)}</p>
          )}
        </div>
      )}
    </section>
  );
}
